// A channel's notification mode (12-unread-notifications.md, rule 9): Default, All messages,
// Mentions & keywords or Mute, as the room's own push rule; the "Notifications ▸" entry of the room
// menus with its submenu, and the bell a row shows when its mode isn't Default.
import { PushRuleActionName, PushRuleKind, TweakName, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useRef, useState } from 'react';
import { Icon } from '../blocks/icons';
import { Menu } from '../blocks/Menu';
import { Spinner } from '../blocks/Wait';
import { describe } from '../core/errors';
import { t, type Key } from '../i18n';
import { chatIcons } from '../chat/icons';
import { modeOf, type Mode } from './lists';

const MODES: [Mode, Key, string][] = [
  ['default', 'notify.default', chatIcons.notifications],
  ['all', 'notify.allMessages', chatIcons.notificationsActive],
  ['mentions', 'notify.mentions', chatIcons.notificationsUnread],
  ['mute', 'notify.mute', chatIcons.notificationsOff],
];
const NAMES: Partial<Record<Mode, Key>> = { all: 'notify.namedAll', mentions: 'notify.namedMentions', mute: 'notify.namedMute' };

/** Removes the room's previous rule, then adds the new one; "Default" only removes. */
async function setMode(client: MatrixClient, roomId: string, mode: Mode) {
  const before = modeOf(client, roomId);
  if (before === 'mute') await client.deletePushRule('global', PushRuleKind.Override, roomId);
  if (before === 'all' || before === 'mentions') await client.deletePushRule('global', PushRuleKind.RoomSpecific, roomId);
  if (mode === 'all') await client.addPushRule('global', PushRuleKind.RoomSpecific, roomId, { actions: [PushRuleActionName.Notify, { set_tweak: TweakName.Sound, value: 'default' }] });
  if (mode === 'mentions') await client.addPushRule('global', PushRuleKind.RoomSpecific, roomId, { actions: [] });
  if (mode === 'mute')
    await client.addPushRule('global', PushRuleKind.Override, roomId, { actions: [], conditions: [{ kind: 'event_match', key: 'room_id', pattern: roomId }] } as never);
}

/** The bell after a row's badge, named in words. */
export function ModeIcon({ client, room }: { client: MatrixClient; room: Room }) {
  const mode = modeOf(client, room.roomId);
  if (mode === 'default') return null;
  return (
    <span className="mode-icon" role="img" aria-label={t(NAMES[mode]!)}>
      <Icon svg={MODES.find(([m]) => m === mode)![2]} size={16} />
    </span>
  );
}

/** "Notifications ▸": its four modes open to the right; a change is saved before the menu closes. */
export function NotificationsItem({ client, room, onDone }: { client: MatrixClient; room: Room; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState<Mode>();
  const [error, setError] = useState<string>();
  const item = useRef<HTMLButtonElement>(null);
  const mode = modeOf(client, room.roomId);
  const choose = async (next: Mode) => {
    setSaving(next);
    setError(undefined);
    try {
      await setMode(client, room.roomId, next);
      onDone();
    } catch (e) {
      setError(describe(e, 'save').text);
    } finally {
      setSaving(undefined);
    }
  };
  return (
    <>
      <button
        ref={item}
        type="button"
        role="menuitem"
        className="menu-item"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'ArrowRight' && setOpen(true)}
      >
        <Icon svg={chatIcons.notifications} size={16} />
        <span className="menu-label">{t('notify.page')}</span>
        {saving ? <Spinner size={16} /> : <Icon svg={MODES.find(([m]) => m === mode)![2]} size={16} />}
      </button>
      {open && (
        <Menu label={t('notify.page')} anchor={item.current} side="right" onClose={() => setOpen(false)}>
          {MODES.map(([m, key, icon]) => (
            <button key={m} type="button" role="menuitemradio" className="menu-item" aria-checked={m === mode} disabled={!!saving} onClick={() => void choose(m)}>
              <Icon svg={icon} size={16} />
              <span className="menu-label">{t(key)}</span>
              {saving === m && <Spinner size={16} />}
            </button>
          ))}
          {error && (
            <p className="menu-error" role="alert">
              {error}
            </p>
          )}
        </Menu>
      )}
    </>
  );
}
